import { useBreakpointValue } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useState } from 'react';

import type { useCallRoom } from '#features/calls/hooks/useCallRoom';
import { pickPhoneCameras } from '#features/media/deviceLabels';
import { useMicLevel } from '#features/media/useMicLevel';
import { TierMicCluster } from '#features/voice/components/SessionVoiceTier/TierCluster';

/** Управление тем же звуком, что в сессии; медиа принадлежат единственному владельцу звонка. */
export const CallMicCluster = ({
  room,
  micEnabled,
  onToggleMic,
  onHangup,
  open,
  onOpenChange,
}: {
  room: ReturnType<typeof useCallRoom>;
  micEnabled: boolean;
  onToggleMic: () => void;
  onHangup: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const { t } = useLingui();
  const [selfOpen, setSelfOpen] = useState(false);
  const pickerOpen = open ?? selfOpen;
  const setOpen = (next: boolean) => {
    setSelfOpen(next);
    onOpenChange?.(next);
  };
  const density =
    useBreakpointValue<'desktop' | 'mobile'>({ base: 'mobile', md: 'desktop' }) ?? 'mobile';
  const micLevel = useMicLevel(
    pickerOpen && micEnabled && !room.mediaError,
    undefined,
    room.microphoneTrack ?? null
  );

  return (
    <TierMicCluster
      density={density}
      micEnabled={micEnabled}
      micDenied={room.mediaError}
      micLevel={micLevel}
      microphoneTrack={room.microphoneTrack}
      meterState={micLevel}
      onToggleMic={onToggleMic}
      onRequestAccess={() =>
        void room.requestMicAccess().then((granted) => {
          if (granted) setOpen(true);
          return granted;
        })
      }
      listDevices={async () => []}
      audioDevices={room.devices}
      speakerSelectionSupported={room.devices?.speakerSelectionSupported}
      activeDeviceIds={{
        audioinput: room.devices?.activeMicrophoneId,
        audiooutput: room.devices?.activeSpeakerId,
      }}
      onSelectDevice={room.switchDevice}
      onOpenDevices={() => void room.refreshDevices()}
      camera={{
        devices: pickPhoneCameras(
          room.devices?.cameras ?? [],
          room.devices?.activeCameraId ?? null
        ),
        activeId: room.devices?.activeCameraId ?? null,
        onSelect: (id) => room.switchDevice('videoinput', id),
      }}
      devicePickerOpen={pickerOpen}
      onDevicePickerOpenChange={setOpen}
      onLeave={onHangup}
      leaveLabel={t`End call`}
    />
  );
};
